import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'
import { AccessDeniedScreen, AccountPendingScreen, AuthErrorScreen, LoadingScreen } from './AuthScreens'
import type { AppRole } from '../../services/auth/roleService'
import { defaultPathForRole, getAuthorizationDecision } from './authResolution'
import { FeatureLockedScreen } from './FeatureLockedScreen'
import { isAdminFeatureEnabled, type AdminFeature } from '../../config/featureFlags'

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const location = useLocation()
  const decision = getAuthorizationDecision(auth)

  if (decision === 'loading') return <LoadingScreen label="Loading your account..." />
  if (decision === 'signed_out') return <Navigate to="/login" replace state={{ from: location }} />
  if (decision === 'error') return <AuthErrorScreen message={auth.error ?? 'Account access could not be checked.'} />
  if (decision === 'pending') return <AccountPendingScreen />
  return children
}

export function RoleGate({ role, children }: { role: AppRole; children: ReactNode }) {
  const auth = useAuth()
  const decision = getAuthorizationDecision(auth, role)

  if (decision === 'loading') return <LoadingScreen label="Loading your account..." />
  if (decision === 'error') return <AuthErrorScreen message={auth.error ?? 'Account access could not be checked.'} />
  if (decision === 'pending') return <AccountPendingScreen />
  if (decision === 'signed_out') return <AccessDeniedScreen />
  if (decision === 'denied' && auth.role) return <Navigate to={defaultPathForRole(auth.role)} replace />
  if (decision === 'denied') return <AccessDeniedScreen />
  return children
}


export function AdminFeatureGate({ feature, children }: { feature: AdminFeature; children: ReactNode }) {
  if (!isAdminFeatureEnabled(feature)) return <FeatureLockedScreen feature={feature} />
  return children
}
